import type { RouteDescriptorInput } from '@perceptio-llm/features/navigation';
import type { WebAppConfig, WebFeatureBinding } from './types';

export function validateAppConfig(config: WebAppConfig): void {
  if (!/^[a-z][a-z0-9-]*$/.test(config.id)) {
    throw new Error(`Invalid App ID: ${config.id}`);
  }
  validateRegisteredId(
    config.shell.public,
    config.registries.shells,
    'public shell',
  );
  validateRegisteredId(
    config.shell.account,
    config.registries.shells,
    'account shell',
  );
  validateRegisteredId(
    config.shell.workspace,
    config.registries.shells,
    'workspace shell',
  );
  validateRegisteredId(
    config.branding.themeId,
    config.registries.themes,
    'theme',
  );
  for (const provider of config.providers ?? []) {
    validateRegisteredId(provider, config.registries.providers, 'provider');
  }

  const features = new Map<string, WebFeatureBinding>();
  const routes = new Map<string, RouteDescriptorInput & { id: string }>();
  const paths = new Map<string, string>();

  for (const binding of config.features) {
    const feature = binding.descriptor;
    if (features.has(feature.id))
      throw new Error(`Duplicate Feature ID: ${feature.id}`);
    features.set(feature.id, binding);

    const screenIds = new Set(Object.keys(binding.screens));
    for (const route of feature.routes) {
      if (routes.has(route.id))
        throw new Error(`Duplicate Route ID: ${route.id}`);
      routes.set(route.id, route);
      if (!screenIds.delete(route.id))
        throw new Error(`Missing Web screen: ${route.id}`);

      const pathKey = `${route.scope}:${normalizePath(route.path)}`;
      const existing = paths.get(pathKey);
      if (existing)
        throw new Error(`Route path conflict: ${existing} and ${route.id}`);
      paths.set(pathKey, route.id);
      validateNavigationBinding(config, route);
    }
    const extraScreen = screenIds.values().next().value as string | undefined;
    if (extraScreen)
      throw new Error(`Unknown Web screen in ${feature.id}: ${extraScreen}`);

    for (const routeId of Object.keys(binding.navigation ?? {})) {
      const route = routes.get(routeId);
      if (!route || !routeId.startsWith(`${feature.id}.`)) {
        throw new Error(
          `Web navigation default targets unknown route in ${feature.id}: ${routeId}`,
        );
      }
      if (!route.navigation) {
        throw new Error(
          `Web navigation default targets non-navigation route: ${routeId}`,
        );
      }
    }
  }

  validateDependencies(features);

  for (const routeId of Object.keys(
    config.shell.workspaceNavigation?.routes ?? {},
  )) {
    const route = routes.get(routeId);
    if (!route) {
      throw new Error(
        `Workspace navigation layout targets disabled route: ${routeId}`,
      );
    }
    if (route.scope !== 'workspace') {
      throw new Error(
        `Workspace navigation layout targets non-workspace route: ${routeId}`,
      );
    }
  }

  for (const routeId of Object.keys(config.navigation?.overrides ?? {})) {
    const route = routes.get(routeId);
    if (!route)
      throw new Error(`Navigation override targets disabled route: ${routeId}`);
    if (!route.navigation)
      throw new Error(
        `Navigation override targets non-navigation route: ${routeId}`,
      );
  }

  for (const target of Object.values(config.routing)) {
    validateRoutingTarget(target.routeId, target.scope, routes);
    if (target.fallbackRouteId)
      validateRoutingTarget(target.fallbackRouteId, target.scope, routes);
  }
}

function validateRegisteredId(
  id: string,
  registered: readonly string[],
  kind: string,
): void {
  if (!registered.includes(id)) throw new Error(`Unknown ${kind} ID: ${id}`);
}

function normalizePath(path: string): string {
  return (
    path.replace(/:[A-Za-z][A-Za-z0-9]*/g, ':param').replace(/\/+$/, '') || '/'
  );
}

function validateNavigationBinding(
  config: WebAppConfig,
  route: RouteDescriptorInput & { id: string },
): void {
  const navigation = route.navigation;
  if (!navigation) return;
  if (!config.registries.groups.includes(navigation.group)) {
    throw new Error(
      `Unknown navigation group in ${route.id}: ${navigation.group}`,
    );
  }
  if (navigation.icon && !config.registries.icons.includes(navigation.icon)) {
    throw new Error(
      `Unknown navigation icon in ${route.id}: ${navigation.icon}`,
    );
  }
  const predicate = navigation.access?.predicate ?? route.access?.predicate;
  if (predicate && !config.registries.predicates.includes(predicate)) {
    throw new Error(
      `Unknown navigation predicate in ${route.id}: ${predicate}`,
    );
  }
}

function validateDependencies(
  features: ReadonlyMap<string, WebFeatureBinding>,
): void {
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (id: string): void => {
    if (visiting.has(id)) throw new Error(`Feature dependency cycle at: ${id}`);
    if (visited.has(id)) return;
    const binding = features.get(id);
    if (!binding) throw new Error(`Missing Feature dependency: ${id}`);
    visiting.add(id);
    for (const dependency of binding.dependencies ?? []) visit(dependency);
    visiting.delete(id);
    visited.add(id);
  };
  for (const id of features.keys()) visit(id);
}

function validateRoutingTarget(
  routeId: string,
  scope: string,
  routes: ReadonlyMap<string, RouteDescriptorInput & { id: string }>,
): void {
  const route = routes.get(routeId);
  if (!route) throw new Error(`Routing target is not enabled: ${routeId}`);
  if (route.scope !== scope) {
    throw new Error(
      `Routing target ${routeId} must use ${scope} scope, received ${route.scope}`,
    );
  }
}
